import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import useTranslation from '../../hooks/useTranslation';
import { canSeeAllProjects } from '../../utils/permissions';
import { getProjectDetail } from '../../services/portfolioProfitService';
import { projectService } from '../../services/projectService';
import * as projectCostService from '../../services/projectCostService';
import { projectExtrasService } from '../../services/projectExtrasService';
import { listRFIsByProject } from '../../services/rfiService';
import * as projectNotesService from '../../services/projectNotesService';
import Icon from '../../components/AppIcon';
import StatsCard from '../../components/ui/StatsCard';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { signedExtraAmount } from '../../utils/extraSign';

// ─── Helpers ────────────────────────────────────────────────────────────────

const LAST_PROJECT_KEY = 'project360_lastProjectId';

const formatCAD = (value) =>
  new Intl.NumberFormat('fr-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })?.format(value || 0);

const formatCAD2 = (value) =>
  new Intl.NumberFormat('fr-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })?.format(value || 0);

const formatPct = (value) =>
  value != null ? `${value?.toFixed(1)} %` : '—';

// ─── Sub-components ──────────────────────────────────────────────────────────

const DpStatusPill = ({ status, t }) => {
  const cfg = {
    approved: { label: t('project360.statusApproved'), cls: 'bg-green-100 text-green-800' },
    submitted: { label: t('project360.statusSubmitted'), cls: 'bg-blue-100 text-blue-800' },
    draft: { label: t('project360.statusDraft'), cls: 'bg-gray-100 text-gray-600' },
  };
  const { label, cls } = cfg?.[status] || { label: status, cls: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${cls}`}>
      {label}
    </span>
  );
};

// ── Profit par DP chart ──────────────────────────────────────────────────────
const ProfitChart = ({ dps, unassignedCosts, t }) => {
  const [hoveredId, setHoveredId] = React.useState(null);

  const sharedMax = Math.max(...(dps?.map((dp) => dp?.billed ?? 0) || [0]), 1);

  // Build 4 axis ticks: 0, 1/3, 2/3, max
  const ticks = [0, sharedMax / 3, (sharedMax * 2) / 3, sharedMax];

  const formatAxisLabel = (val) => {
    if (val === 0) return '0';
    if (val >= 1000) return `${Math.round(val / 1000)} k`;
    return Math.round(val)?.toString();
  };

  const formatCADFull = (v) =>
    new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })?.format(v ?? 0);

  return (
    <div className="flex flex-col">
      {/* Legend */}
      <div className="flex items-center gap-3 px-3 pt-2 pb-1 shrink-0">
        <div className="flex items-center gap-1">
          <span className="inline-block w-2.5 h-1.5 rounded-sm bg-[#2563EB]" />
          <span className="text-[10px] text-gray-400 font-normal">Facturé</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="inline-block w-2.5 h-1.5 rounded-sm bg-[#94A3B8]" />
          <span className="text-[10px] text-gray-400 font-normal">Coûts</span>
        </div>
      </div>

      {/* Rows area — no scroll, auto height */}
      <div className="px-3 pb-1 space-y-1">
        {dps?.length === 0 ? (
          <p className="text-[12px] text-gray-400 text-center py-6">{t('project360.noDps')}</p>
        ) : (
          dps?.map((dp) => {
            const isHovered = hoveredId === dp?.id;
            const marginDelta = (dp?.billed ?? 0) - (dp?.cost ?? 0);
            const billedPct = ((dp?.billed ?? 0) / sharedMax) * 100;
            const costPct = ((dp?.cost ?? 0) / sharedMax) * 100;

            return (
              <div
                key={dp?.id}
                className={`rounded px-1 py-0.5 transition-colors ${isHovered ? 'bg-gray-50' : ''}`}
                onMouseEnter={() => setHoveredId(dp?.id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{ position: 'relative', display: 'grid', gridTemplateColumns: '150px minmax(0,1fr) 92px 64px', alignItems: 'center', gap: '0 8px' }}
              >
                {/* COL 1: DP label + status pill */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-[11px] font-medium text-gray-600 leading-none whitespace-nowrap">DP{dp?.number}</span>
                  <DpStatusPill status={dp?.status} t={t} />
                </div>

                {/* COL 2: CSS bars — single position:relative plot container */}
                <div style={{ position: 'relative', height: 16 }}>
                  {/* Gridlines (behind bars) */}
                  {ticks?.map((tick, i) => {
                    const leftPct = (tick / sharedMax) * 100;
                    return (
                      <div
                        key={i}
                        style={{
                          position: 'absolute',
                          left: `${leftPct}%`,
                          top: 0,
                          bottom: 0,
                          width: 1,
                          background: '#E5E7EB',
                          pointerEvents: 'none',
                        }}
                      />
                    );
                  })}
                  {/* Facturé track */}
                  <div style={{ position: 'absolute', left: 0, top: 1, width: '100%', height: 7, background: '#F1F3F5', borderRadius: 0 }} />
                  {/* Facturé bar */}
                  <div style={{ position: 'absolute', left: 0, top: 1, width: `${billedPct}%`, height: 7, background: '#2563EB', borderRadius: 0 }} />
                  {/* Coûts track */}
                  <div style={{ position: 'absolute', left: 0, top: 10, width: '100%', height: 7, background: '#F1F3F5', borderRadius: 0 }} />
                  {/* Coûts bar */}
                  <div style={{ position: 'absolute', left: 0, top: 10, width: `${costPct}%`, height: 7, background: '#94A3B8', borderRadius: 0 }} />
                </div>

                {/* COL 3: profit (marge $) — right-aligned, fixed 92px */}
                <div className="flex items-center justify-end">
                  <span
                    className="text-[12px] font-medium tabular-nums leading-none whitespace-nowrap"
                    style={{ color: (dp?.delta ?? 0) < 0 ? '#B91C1C' : '#15803D' }}
                  >
                    {formatCAD(dp?.delta)}
                  </span>
                </div>

                {/* COL 4: margin pill — fixed 64px */}
                <div className="flex items-center justify-end">
                  <MarginPill pct={dp?.marginPct} />
                </div>

                {/* Hover tooltip */}
                {isHovered && (
                  <div
                    className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-[11px] text-gray-700 whitespace-nowrap"
                    style={{ minWidth: 180 }}
                  >
                    <div className="font-semibold text-gray-900 mb-1">DP{dp?.number}</div>
                    <div className="flex justify-between gap-4">
                      <span className="text-gray-500">Facturé</span>
                      <span className="font-medium tabular-nums">{formatCADFull(dp?.billed)}</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-gray-500">Coûts</span>
                      <span className="font-medium tabular-nums">{formatCADFull(dp?.cost)}</span>
                    </div>
                    <div className="flex justify-between gap-4 border-t border-gray-100 mt-1 pt-1">
                      <span className="text-gray-500">Marge $</span>
                      <span className={`font-semibold tabular-nums ${marginDelta < 0 ? 'text-red-600' : 'text-green-700'}`}>{formatCADFull(marginDelta)}</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-gray-500">Marge %</span>
                      <span className="font-medium tabular-nums">{dp?.marginPct != null ? `${dp?.marginPct?.toFixed(1)} %` : '—'}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* X-axis — same 4-column grid; tick labels inside the PLOT column (col 2) */}
        {dps?.length > 0 && (
          <div className="pt-1 pb-2" style={{ display: 'grid', gridTemplateColumns: '150px minmax(0,1fr) 92px 64px', gap: '0 8px' }}>
            {/* spacer col 1 */}
            <div />
            {/* col 2: tick labels inside one position:relative container = same width as bars */}
            <div style={{ position: 'relative', height: 16 }}>
              {ticks?.map((tick, i) => {
                const leftPct = (tick / sharedMax) * 100;
                const isFirst = i === 0;
                const isLast = i === ticks?.length - 1;
                return (
                  <span
                    key={i}
                    style={{
                      position: 'absolute',
                      left: `${leftPct}%`,
                      top: 0,
                      fontSize: 10,
                      color: '#9CA3AF',
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                      transform: isFirst ? 'none' : isLast ? 'translateX(-100%)' : 'translateX(-50%)',
                    }}
                  >
                    {formatAxisLabel(tick)}
                  </span>
                );
              })}
            </div>
            {/* spacers col 3 + col 4 */}
            <div />
            <div />
          </div>
        )}

        {/* Unassigned costs note — inside the card, amber style */}
        {unassignedCosts > 0 && (
          <div className="mx-0 mt-2 mb-1 px-2 py-1.5 rounded border border-amber-200 bg-amber-50">
            <p className="text-[11px] text-amber-700">
              ⚠️ {t('project360.unassignedNote', { amount: formatCAD(unassignedCosts) })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const MarginPill = ({ pct }) => {
  if (pct == null) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-500 tabular-nums whitespace-nowrap">
        —
      </span>
    );
  }
  let cls = 'bg-[#DCFCE7] text-[#15803D]';
  if (pct < 0) cls = 'bg-[#FEE2E2] text-[#B91C1C]';
  else if (pct < 10) cls = 'bg-[#FEF3C7] text-[#B45309]';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold tabular-nums whitespace-nowrap ${cls}`}>
      {formatPct(pct)}
    </span>
  );
};

const AgePill = ({ days }) => {
  if (days == null) return <span className="text-[11px] text-gray-400">—</span>;
  let cls = 'bg-green-100 text-green-800';
  if (days > 30) cls = 'bg-red-100 text-red-800';
  else if (days > 7) cls = 'bg-amber-100 text-amber-800';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium tabular-nums ${cls}`}>
      {days}j
    </span>
  );
};

// Mini stat tile used inside cards
const MiniStat = ({ label, value, valueColor = 'text-gray-900' }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide leading-none">{label}</span>
    <span className={`text-[14px] font-bold tabular-nums leading-tight ${valueColor}`}>{value}</span>
  </div>
);

// Extra status pill
const ExtraStatusPill = ({ status }) => {
  const cfg = {
    approved: { label: 'Approuvé', cls: 'bg-green-100 text-green-800' },
    submitted: { label: 'Soumis', cls: 'bg-blue-100 text-blue-800' },
    draft: { label: 'Brouillon', cls: 'bg-gray-100 text-gray-600' },
    rejected: { label: 'Rejeté', cls: 'bg-red-100 text-red-800' },
  };
  const { label, cls } = cfg?.[status] || { label: status, cls: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${cls}`}>
      {label}
    </span>
  );
};

// RFI status pill
const RfiStatusPill = ({ status }) => {
  const cfg = {
    draft: { label: 'Brouillon', cls: 'bg-gray-100 text-gray-600' },
    sent_for_approval: { label: 'Soumis', cls: 'bg-blue-100 text-blue-800' },
    in_review: { label: 'En révision', cls: 'bg-amber-100 text-amber-800' },
    answered: { label: 'Répondu', cls: 'bg-green-100 text-green-800' },
  };
  const { label, cls } = cfg?.[status] || { label: status, cls: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${cls}`}>
      {label}
    </span>
  );
};

// ─── CardNotesFooter ─────────────────────────────────────────────────────────

const CardNotesFooter = ({ cardKey, notes, userProfile, projectId, onNotesChange, t, language }) => {
  const [expanded, setExpanded] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const cardNotes = notes || [];
  const count = cardNotes?.length;

  const handleAdd = async () => {
    if (!inputValue?.trim()) return;
    setAdding(true);
    try {
      await projectNotesService?.addNote(
        { projectId, cardKey, note: inputValue?.trim() },
        userProfile
      );
      setInputValue('');
      await onNotesChange();
    } catch (e) {
      // silently fail
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await projectNotesService?.deleteNote(id);
      await onNotesChange();
    } catch (e) {
      // silently fail
    } finally {
      setDeletingId(null);
    }
  };

  const formatNoteDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d?.toLocaleDateString(language === 'en' ? 'en-CA' : 'fr-CA', {
      day: 'numeric',
      month: 'short',
    }) + ' ' + d?.toLocaleTimeString(language === 'en' ? 'en-CA' : 'fr-CA', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  return (
    <div className="border-t border-gray-100 px-3 py-2 shrink-0">
      {/* Toggle row */}
      <button
        className="flex items-center justify-between w-full text-left"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="text-[11px] text-primary font-medium">
          {count > 0
            ? `🗒 ${t('project360.notesTitle')} (${count})`
            : `🗒 ${t('project360.notesPlaceholder')}`}
        </span>
        <span className="text-[11px] text-primary ml-1">{expanded ? '▲' : '▼'}</span>
      </button>

      {/* Expanded area */}
      {expanded && (
        <div className="mt-2 flex flex-col gap-2">
          {/* Input row */}
          <div className="flex items-center gap-1.5">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e?.target?.value)}
              onKeyDown={(e) => { if (e?.key === 'Enter') handleAdd(); }}
              placeholder={t('project360.notesPlaceholder')}
              className="flex-1 text-[11px] border border-gray-200 rounded px-2 py-1 outline-none focus:border-primary bg-white"
              disabled={adding}
            />
            <button
              onClick={handleAdd}
              disabled={!inputValue?.trim() || adding}
              className="text-[11px] px-2 py-1 rounded bg-gray-100 text-gray-600 font-medium hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {t('project360.notesAdd')}
            </button>
          </div>

          {/* Notes log */}
          <div
            className="overflow-y-auto flex flex-col gap-1"
            style={{ maxHeight: 120 }}
          >
            {cardNotes?.length === 0 ? (
              <p className="text-[11px] text-gray-400 italic">{t('project360.notesEmpty')}</p>
            ) : (
              cardNotes?.map((n) => (
                <div key={n?.id} className="flex items-start justify-between gap-1 group">
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="text-[10px] text-gray-400 leading-none">
                      {n?.author_name || '—'} · {formatNoteDate(n?.created_at)}
                    </span>
                    <span className="text-[12px] text-gray-700 leading-snug break-words">{n?.note}</span>
                  </div>
                  {n?.created_by === userProfile?.id && (
                    <button
                      onClick={() => handleDelete(n?.id)}
                      disabled={deletingId === n?.id}
                      className="shrink-0 text-[11px] text-gray-300 hover:text-red-400 disabled:opacity-40 transition-colors leading-none mt-0.5"
                      title="Supprimer"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────

const Project360Page = () => {
  const { projectId } = useParams();
  const { userProfile } = useAuth();
  const { t, language } = useTranslation();
  const navigate = useNavigate();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allProjects, setAllProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // New card states
  const [overrunCosts, setOverrunCosts] = useState([]);
  const [overrunLoading, setOverrunLoading] = useState(false);
  const [extras, setExtras] = useState([]);
  const [extrasLoading, setExtrasLoading] = useState(false);
  const [rfis, setRfis] = useState([]);
  const [rfisLoading, setRfisLoading] = useState(false);

  // Notes state
  const [allNotes, setAllNotes] = useState([]);

  // Permission gate
  useEffect(() => {
    if (userProfile && !canSeeAllProjects(userProfile)) {
      navigate('/project-detail', { replace: true });
    }
  }, [userProfile, navigate]);

  // Load all projects for the selector
  useEffect(() => {
    const load = async () => {
      setProjectsLoading(true);
      try {
        const projects = await projectService?.getAll({
          excludeStatuses: ['deleted', 'archived', 'cancelled'],
        });
        setAllProjects(projects || []);
      } catch (e) {
        setAllProjects([]);
      } finally {
        setProjectsLoading(false);
      }
    };
    load();
  }, []);

  // When no projectId (opened via /vue-projet), redirect to last remembered or first project
  useEffect(() => {
    if (!projectId && !projectsLoading && allProjects?.length > 0) {
      const lastId = localStorage.getItem(LAST_PROJECT_KEY);
      const lastExists = lastId && allProjects?.some((p) => p?.id === lastId);
      const targetId = lastExists ? lastId : allProjects?.[0]?.id;
      navigate(`/vue-projet/${targetId}`, { replace: true });
    }
  }, [projectId, projectsLoading, allProjects, navigate]);

  // Persist last selected project
  useEffect(() => {
    if (projectId) {
      localStorage.setItem(LAST_PROJECT_KEY, projectId);
    }
  }, [projectId]);

  // Load project detail when projectId changes
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const result = await getProjectDetail(projectId);
      if (!cancelled) {
        setDetail(result);
        setLoading(false);
        setLastRefreshed(new Date());
      }
    };
    load();
    return () => { cancelled = true; };
  }, [projectId]);

  // Load notes when projectId changes
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    const loadNotes = async () => {
      try {
        const data = await projectNotesService?.getByProject(projectId);
        if (!cancelled) setAllNotes(data || []);
      } catch (e) {
        if (!cancelled) setAllNotes([]);
      }
    };
    loadNotes();
    return () => { cancelled = true; };
  }, [projectId]);

  // Manual refresh handler
  const handleRefresh = async () => {
    if (!projectId || refreshing) return;
    setRefreshing(true);
    try {
      const result = await getProjectDetail(projectId);
      setDetail(result);
      setLastRefreshed(new Date());
    } finally {
      setRefreshing(false);
    }
  };

  // Load budget overruns, extras, and RFIs when projectId changes
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    // Budget at-risk watchlist
    const loadOverruns = async () => {
      setOverrunLoading(true);
      try {
        const costs = await projectCostService?.getByProjectId(projectId);
        if (!cancelled) {
          const atRisk = (costs || [])
            ?.map((c) => {
              const budget = c?.budget || 0;
              const actual = c?.actual || 0;
              const ecart = budget - actual;
              const marginPct = budget > 0 ? (ecart / budget) * 100 : null;
              return { ...c, budget, actual, ecart, marginPct };
            })
            ?.filter((c) => {
              // Show if marginPct < 10 (includes negatives), OR budget=0 with actual>0
              if (c?.budget === 0 && c?.actual > 0) return true;
              if (c?.marginPct != null && c?.marginPct < 10) return true;
              return false;
            })
            ?.sort((a, b) => {
              // Sort ascending by marginPct (most negative / worst first)
              // budget=0 lines treated as worst (null marginPct → -Infinity)
              const aPct = a?.marginPct ?? -Infinity;
              const bPct = b?.marginPct ?? -Infinity;
              return aPct - bPct;
            });
          setOverrunCosts(atRisk);
        }
      } catch (e) {
        if (!cancelled) setOverrunCosts([]);
      } finally {
        if (!cancelled) setOverrunLoading(false);
      }
    };

    // Extras
    const loadExtras = async () => {
      setExtrasLoading(true);
      try {
        const data = await projectExtrasService?.getByProjectId(projectId);
        if (!cancelled) setExtras(data || []);
      } catch (e) {
        if (!cancelled) setExtras([]);
      } finally {
        if (!cancelled) setExtrasLoading(false);
      }
    };

    // RFIs
    const loadRfis = async () => {
      setRfisLoading(true);
      try {
        const { data } = await listRFIsByProject(projectId);
        if (!cancelled) setRfis(data || []);
      } catch (e) {
        if (!cancelled) setRfis([]);
      } finally {
        if (!cancelled) setRfisLoading(false);
      }
    };

    loadOverruns();
    loadExtras();
    loadRfis();

    return () => { cancelled = true; };
  }, [projectId]);

  const refreshNotes = async () => {
    if (!projectId) return;
    try {
      const data = await projectNotesService?.getByProject(projectId);
      setAllNotes(data || []);
    } catch (e) {
      // silently fail
    }
  };

  // Helper: get notes for a specific card_key
  const notesForCard = (cardKey) => allNotes?.filter((n) => n?.card_key === cardKey);

  const handleProjectChange = (id) => {
    if (id) navigate(`/vue-projet/${id}`);
  };

  if (!userProfile || !canSeeAllProjects(userProfile)) return null;

  // Show loading spinner while projects are loading or while redirecting (no projectId)
  if (!projectId) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary" />
      </div>
    );
  }

  const project = detail?.project;
  const kpis = detail?.kpis;
  const dps = detail?.dps || [];
  const unassignedCosts = detail?.unassignedCosts || 0;
  const facturation = detail?.facturation || {};

  // Sort DPs desc by number for Facturation card
  const dpsSortedDesc = [...dps]?.sort((a, b) => (b?.number ?? 0) - (a?.number ?? 0));

  // Project display string
  const projectLabel = project
    ? (project?.projectNumber ? `${project?.projectNumber} · ${project?.name}` : project?.name)
    : null;

  // Team info
  const hasPm = !!project?.pmName;
  const hasCoord = !!project?.coordinatorName;
  const teamLine = [
    hasPm ? `${t('project360.teamPm')} ${project?.pmName}` : null,
    hasCoord ? `${t('project360.teamCoordinator')} ${project?.coordinatorName}` : null,
  ]?.filter(Boolean)?.join('  ·  ');

  // ── Extras KPIs ──
  // Le signe vient du TYPE (voir src/utils/extraSign.js) : la somme
  // additionnait les credits au lieu de les soustraire.
  const extrasApproved = extras?.filter((e) => e?.status === 'approved')
    ?.reduce((s, e) => s + signedExtraAmount(e, e?.costAccepted ?? e?.amount), 0);
  const extrasPending = extras?.filter((e) => ['draft', 'submitted', 'to_resubmit']?.includes(e?.status))
    ?.reduce((s, e) => s + (parseFloat(e?.amount) || 0), 0);
  const approvedWithProfit = extras?.filter((e) => e?.status === 'approved' && e?.profit != null && e?.amount > 0);
  const avgMarginCO = approvedWithProfit?.length > 0
    ? approvedWithProfit?.reduce((s, e) => {
        const base = parseFloat(e?.amount) || 0;
        const profit = parseFloat(e?.profit) || 0;
        return s + (base > 0 ? (profit / base) * 100 : 0);
      }, 0) / approvedWithProfit?.length
    : null;

  // ── RFI KPIs ──
  const openStatuses = ['draft', 'sent_for_approval', 'in_review'];
  const openRfis = rfis?.filter((r) => r?.status !== 'answered');
  const noResponseRfis = rfis?.filter((r) => openStatuses?.includes(r?.status));
  const answeredRfis = rfis?.filter((r) => r?.status === 'answered' && r?.answered_at && r?.created_at);
  const avgResponseDays = answeredRfis?.length > 0
    ? Math.round(
        answeredRfis?.reduce((s, r) => {
          const diff = (new Date(r?.answered_at) - new Date(r?.created_at)) / (1000 * 60 * 60 * 24);
          return s + diff;
        }, 0) / answeredRfis?.length
      )
    : null;

  const today = new Date();
  const rfiAgeDays = (rfi) => {
    if (!rfi?.created_at) return null;
    return Math.floor((today - new Date(rfi?.created_at)) / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="pt-0 pb-0 px-0">

      {/* ── Header Block — mirrors BudgetPage layout ── */}
      <div className="mb-4">
        <div className="flex items-start justify-between gap-6">

          {/* Left Side: Title + project info */}
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="LayoutDashboard" size={20} className="text-primary" />
              <h1 className="text-lg font-semibold text-[#2C3E50]">
                {t('project360.title')}
              </h1>
            </div>
            {projectLabel && (
              <div className="flex items-center gap-1 mb-1">
                <span className="text-sm text-primary font-normal">
                  {project?.projectNumber || ''}
                </span>
                {project?.projectNumber && (
                  <span className="text-sm text-primary font-normal">-</span>
                )}
                <span className="text-sm text-primary font-normal">
                  {project?.name || ''}
                </span>
              </div>
            )}
            <p className="text-xs text-gray-500">
              {t('project360.subtitle')}
            </p>
            {teamLine ? (
              <p className="text-[11px] text-gray-400 mt-0.5">{teamLine}</p>
            ) : null}
          </div>

          {/* Right Side: KPI StatsCards — same grid as BudgetPage */}
          <div className="flex-1 flex flex-col items-end gap-2">
            {/* KPI StatsCards */}
            <div className="flex items-center justify-end gap-2 flex-wrap">
            <StatsCard
              title={t('project360.kpiContrat')}
              value={formatCAD(kpis?.contrat)}
              iconName="FileText"
              iconColor="text-blue-500"
              valueColor="text-blue-500"
              titleWeight="font-medium"
              sublabel=""
            />
            <StatsCard
              title={t('project360.kpiBilled')}
              value={formatCAD(kpis?.billed)}
              iconName="Receipt"
              iconColor="text-purple-600"
              valueColor="text-purple-600"
              titleWeight="font-medium"
              sublabel=""
            />
            <StatsCard
              title={t('project360.kpiCosts')}
              value={formatCAD(kpis?.costs)}
              iconName="TrendingDown"
              iconColor="text-orange-500"
              valueColor="text-gray-900"
              titleWeight="font-medium"
              sublabel=""
            />
            <StatsCard
              title={t('project360.kpiMargin')}
              value={formatCAD(kpis?.margin)}
              iconName={(kpis?.margin ?? 0) >= 0 ? 'TrendingUp' : 'TrendingDown'}
              iconColor={(kpis?.margin ?? 0) >= 0 ? 'text-green-500' : 'text-red-500'}
              valueColor={(kpis?.margin ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}
              titleWeight="font-semibold"
              sublabel=""
            />
            <StatsCard
              title={t('project360.kpiMarginPct')}
              value={kpis?.marginPct != null ? `${(kpis?.marginPct ?? 0)?.toFixed(1)}%` : '—'}
              iconName="Percent"
              iconColor="text-purple-500"
              valueColor={
                kpis?.marginPct == null
                  ? 'text-gray-400'
                  : kpis?.marginPct < 0
                  ? 'text-[#B91C1C]'
                  : kpis?.marginPct < 10
                  ? 'text-[#B45309]'
                  : 'text-[#15803D]'
              }
              titleWeight="font-medium"
              sublabel=""
            />
            </div>
          </div>
        </div>
      </div>

      {/* ── Project Selector Row — below header, left-aligned ── */}
      <div className="mb-4 flex items-center gap-2">
        <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
          {t('project360.selectorLabel')}
        </label>
        <SearchableSelect
          options={allProjects?.map((p) => ({
            value: p?.id,
            label: p?.projectNumber ? `${p?.projectNumber} · ${p?.name}` : p?.name,
          }))}
          value={projectId || ''}
          onChange={handleProjectChange}
          placeholder={t('project360.selectorPlaceholder')}
          disabled={projectsLoading}
          className="max-w-[320px] w-full"
        />
        {/* Refresh indicator — right-aligned on same line as project selector */}
        <div className="ml-auto flex items-center gap-2">
          {lastRefreshed && (
            <span className="text-[11px] text-gray-400">
              {t('project360.refreshedAt', { time: lastRefreshed?.toLocaleTimeString(language === 'en' ? 'en-CA' : 'fr-CA', { hour: '2-digit', minute: '2-digit', hour12: false }) })}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="flex items-center gap-1 text-[11px] text-primary hover:text-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <span className={refreshing ? 'animate-spin inline-block' : ''}>↻</span>
            <span>{language === 'en' ? 'Refresh' : 'Actualiser'}</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary" />
        </div>
      ) : !detail ? (
        <div className="p-12 text-center bg-white rounded-lg border border-gray-200 shadow-sm">
          <Icon name="FolderOpen" size={48} className="mx-auto mb-4 text-[#6B7280]" />
          <p className="text-[#6B7280] text-sm">{t('project360.noData')}</p>
        </div>
      ) : (
        <>
          {/* ── CARD 1: Profit par DP — full width, auto height, own row ── */}
          <div
            id="profit-par-dp"
            className="border border-gray-200 rounded-lg bg-white shadow-sm mb-3"
          >
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <h3 className="text-[12px] font-semibold text-[#2C3E50] uppercase tracking-wide">
                  {t('project360.cardProfitParDp')}
                </h3>
                {notesForCard('profit-par-dp')?.length > 0 && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600">
                    🗒 {notesForCard('profit-par-dp')?.length}
                  </span>
                )}
              </div>
              <Link
                to={`/project/${projectId}/client-invoices`}
                className="text-[11px] text-primary hover:underline font-medium"
              >
                {t('project360.moreDetails')}
              </Link>
            </div>
            <ProfitChart dps={dps} unassignedCosts={unassignedCosts} t={t} />
            <CardNotesFooter
              cardKey="profit-par-dp"
              notes={notesForCard('profit-par-dp')}
              userProfile={userProfile}
              projectId={projectId}
              onNotesChange={refreshNotes}
              t={t}
              language={language}
            />
          </div>

          {/* Card Grid — 2 columns, fixed height, same card styling as BudgetPage */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

            {/* ── CARD 2: Facturation & retenues ── */}
            <div
              id="facturation-retenues"
              className="border border-gray-200 rounded-lg bg-white shadow-sm flex flex-col"
              style={{ height: 280 }}
            >
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 shrink-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-[12px] font-semibold text-[#2C3E50] uppercase tracking-wide">
                    {t('project360.cardFacturation')}
                  </h3>
                  {notesForCard('facturation-retenues')?.length > 0 && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600">
                      🗒 {notesForCard('facturation-retenues')?.length}
                    </span>
                  )}
                </div>
                <Link
                  to={`/project/${projectId}/client-invoices`}
                  className="text-[11px] text-primary hover:underline font-medium"
                >
                  {t('project360.moreDetails')}
                </Link>
              </div>
              <div className="flex-1 overflow-y-auto px-3 py-2 flex flex-col gap-2">
                <div className="flex gap-4 pb-2 border-b border-gray-100 shrink-0">
                  <MiniStat
                    label={t('project360.enAttenteApprobation')}
                    value={formatCAD(facturation?.enAttenteApprobation)}
                    valueColor="text-[#2563EB]"
                  />
                  <MiniStat
                    label={t('project360.retenueAccumulee')}
                    value={formatCAD(facturation?.retenueAccumulee)}
                    valueColor="text-gray-700"
                  />
                </div>
                {dpsSortedDesc?.length === 0 ? (
                  <p className="text-[12px] text-gray-400 text-center py-4">{t('project360.noDps')}</p>
                ) : (
                  <table className="w-full text-[12px] tabular-nums">
                    <thead>
                      <tr className="text-[10px] text-gray-400 uppercase tracking-wide">
                        <th className="text-left pb-1 font-semibold">{t('project360.colDp')}</th>
                        <th className="pr-2 text-right pb-1 font-semibold">{t('project360.colSousTotal')}</th>
                        <th className="pr-2 text-right pb-1 font-semibold">{t('project360.colRetenue')}</th>
                        <th className="pr-2 text-right pb-1 font-semibold">{t('project360.colFacture')}</th>
                        <th className="text-right pb-1 font-semibold">{t('project360.colStatut')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {dpsSortedDesc?.map((dp) => (
                        <tr key={dp?.id} className="hover:bg-gray-50">
                          <td className="py-1 pr-2 font-medium text-gray-800">DP{dp?.number}</td>
                          <td className="py-1 pr-2 text-right text-[12px] font-medium text-gray-800 tabular-nums">{formatCAD2(dp?.subtotal)}</td>
                          <td className="py-1 pr-2 text-right text-[12px] font-medium text-gray-800 tabular-nums">{formatCAD2(dp?.retenue)}</td>
                          <td className="py-1 pr-2 text-right">
                            <div className="flex flex-col items-end gap-0">
                              <span className="text-[12px] font-semibold text-gray-900 tabular-nums">
                                {formatCAD2(dp?.avantTaxes)}
                              </span>
                              {dp?.totalAvecTaxes != null && (
                                <span className="text-[10px] font-normal text-gray-400 tabular-nums leading-tight">
                                  {language === 'en' ? 'Total with taxes' : 'Total avec taxes'} : {formatCAD2(dp?.totalAvecTaxes)}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-1 text-right"><DpStatusPill status={dp?.status} t={t} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <CardNotesFooter
                cardKey="facturation-retenues"
                notes={notesForCard('facturation-retenues')}
                userProfile={userProfile}
                projectId={projectId}
                onNotesChange={refreshNotes}
                t={t}
                language={language}
              />
            </div>

            {/* ── CARD 3: Budget vs réel — à surveiller ── */}
            <div
              id="budget-depassements"
              className="border border-gray-200 rounded-lg bg-white shadow-sm flex flex-col"
              style={{ height: 280 }}
            >
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 shrink-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-[12px] font-semibold text-[#2C3E50] uppercase tracking-wide">
                    {language === 'en' ?'Budget vs actual — at risk' :'Budget vs réel — à surveiller'}
                  </h3>
                  {!overrunLoading && overrunCosts?.length > 0 && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700">
                      {overrunCosts?.length}
                    </span>
                  )}
                  {notesForCard('budget-depassements')?.length > 0 && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600">
                      🗒 {notesForCard('budget-depassements')?.length}
                    </span>
                  )}
                </div>
                <Link
                  to={`/project/${projectId}/budget`}
                  className="text-[11px] text-primary hover:underline font-medium"
                >
                  {t('project360.moreDetails')}
                </Link>
              </div>
              <div className="flex-1 overflow-y-auto px-3 py-2">
                {overrunLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                  </div>
                ) : overrunCosts?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full gap-1">
                    <Icon name="CheckCircle" size={24} className="text-green-500" />
                    <p className="text-[12px] text-green-700 font-medium">Aucun dépassement ✓</p>
                  </div>
                ) : (
                  <table className="w-full text-[12px] tabular-nums">
                    <thead>
                      <tr className="text-[10px] text-gray-400 uppercase tracking-wide">
                        <th className="text-left pb-1 font-semibold">Code / Description</th>
                        <th className="text-right pb-1 font-semibold">Budget</th>
                        <th className="text-right pb-1 font-semibold">Réel</th>
                        <th className="text-right pb-1 font-semibold">Écart $</th>
                        <th className="text-right pb-1 font-semibold">%</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {overrunCosts?.map((c) => {
                        const ecart = c?.ecart ?? ((c?.budget || 0) - (c?.actual || 0));
                        const marginPct = c?.marginPct ?? (c?.budget > 0 ? (ecart / c?.budget) * 100 : null);
                        // Color: red when over budget (marginPct < 0 or budget=0), amber when 0–<10
                        const isOverBudget = marginPct == null || marginPct < 0;
                        const ecartColor = isOverBudget ? 'text-[#B91C1C]' : 'text-[#B45309]';
                        const pctBg = isOverBudget ? 'bg-[#FEE2E2] text-[#B91C1C]' : 'bg-[#FEF3C7] text-[#B45309]';
                        // Format signed écart: negative shows as "-6 715 $"
                        const ecartFormatted = new Intl.NumberFormat('fr-CA', {
                          style: 'currency',
                          currency: 'CAD',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })?.format(ecart);
                        // Format signed pct: "-7,6 %" or "+5,0 %"
                        const pctFormatted = marginPct != null
                          ? (marginPct >= 0 ? '+' : '') + marginPct?.toFixed(1)?.replace('.', ',') + ' %'
                          : null;
                        return (
                          <tr key={c?.id} className="hover:bg-red-50">
                            <td className="py-1 pr-2 text-gray-800 max-w-[120px]">
                              <span className="text-[12px] font-medium text-gray-800 tabular-nums">{c?.costCode}</span>
                              {c?.description && (
                                <span className="text-[10px] font-normal text-gray-400 leading-tight"> — {c?.description}</span>
                              )}
                            </td>
                            <td className="py-1 pr-2 text-right text-[12px] font-medium text-gray-800 tabular-nums">{formatCAD(c?.budget)}</td>
                            <td className="py-1 pr-2 text-right text-[12px] font-medium text-gray-800 tabular-nums">{formatCAD(c?.actual)}</td>
                            <td className={`py-1 pr-2 text-right text-[12px] font-semibold tabular-nums ${ecartColor}`}>
                              {ecartFormatted}
                            </td>
                            <td className="py-1 text-right font-semibold">
                              {pctFormatted != null ? (
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${pctBg}`}>
                                  {pctFormatted}
                                </span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              <CardNotesFooter
                cardKey="budget-depassements"
                notes={notesForCard('budget-depassements')}
                userProfile={userProfile}
                projectId={projectId}
                onNotesChange={refreshNotes}
                t={t}
                language={language}
              />
            </div>

            {/* ── CARD 4: Extras / Changements ── */}
            <div
              id="extras-changements"
              className="border border-gray-200 rounded-lg bg-white shadow-sm flex flex-col"
              style={{ height: 280 }}
            >
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 shrink-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-[12px] font-semibold text-[#2C3E50] uppercase tracking-wide">
                    Extras / Changements
                  </h3>
                  {notesForCard('extras-changements')?.length > 0 && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600">
                      🗒 {notesForCard('extras-changements')?.length}
                    </span>
                  )}
                </div>
                <Link
                  to={`/project/${projectId}/extras`}
                  className="text-[11px] text-primary hover:underline font-medium"
                >
                  {t('project360.moreDetails')}
                </Link>
              </div>
              <div className="flex-1 overflow-y-auto px-3 py-2 flex flex-col gap-2">
                {extrasLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                  </div>
                ) : (
                  <>
                    {/* Top stats */}
                    <div className="flex gap-4 pb-2 border-b border-gray-100 shrink-0 flex-wrap">
                      <MiniStat
                        label="Approuvés"
                        value={formatCAD(extrasApproved)}
                        valueColor="text-green-700"
                      />
                      <MiniStat
                        label="En attente"
                        value={formatCAD(extrasPending)}
                        valueColor="text-amber-600"
                      />
                      {avgMarginCO != null && (
                        <MiniStat
                          label="Marge CO moy."
                          value={`${avgMarginCO?.toFixed(1)}%`}
                          valueColor="text-gray-700"
                        />
                      )}
                    </div>

                    {/* Scrollable list */}
                    {extras?.length === 0 ? (
                      <p className="text-[12px] text-gray-400 text-center py-4">Aucun extra</p>
                    ) : (
                      <table className="w-full text-[12px] tabular-nums">
                        <thead>
                          <tr className="text-[10px] text-gray-400 uppercase tracking-wide">
                            <th className="text-left pb-1 font-semibold">Extra / CO</th>
                            <th className="text-right pb-1 font-semibold">Montant</th>
                            <th className="text-right pb-1 font-semibold">Statut</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {extras?.map((e) => (
                            <tr key={e?.id} className="hover:bg-gray-50">
                              <td className="py-1 pr-2 text-[12px] font-medium text-gray-800 truncate max-w-[140px]">
                                {e?.title || e?.description || `Extra #${e?.id?.slice(0, 6)}`}
                              </td>
                              <td className="py-1 pr-2 text-right text-[12px] font-semibold text-gray-900 tabular-nums">
                                {formatCAD(signedExtraAmount(e, e?.costAccepted ?? e?.amount))}
                              </td>
                              <td className="py-1 text-right">
                                <ExtraStatusPill status={e?.status} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </>
                )}
              </div>
              <CardNotesFooter
                cardKey="extras-changements"
                notes={notesForCard('extras-changements')}
                userProfile={userProfile}
                projectId={projectId}
                onNotesChange={refreshNotes}
                t={t}
                language={language}
              />
            </div>

            {/* ── CARD 5: RFI ── */}
            <div
              id="rfi"
              className="border border-gray-200 rounded-lg bg-white shadow-sm flex flex-col"
              style={{ height: 280 }}
            >
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 shrink-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-[12px] font-semibold text-[#2C3E50] uppercase tracking-wide">
                    {t('rfi.pageTitle')}
                  </h3>
                  {notesForCard('rfi')?.length > 0 && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600">
                      🗒 {notesForCard('rfi')?.length}
                    </span>
                  )}
                </div>
                <Link
                  to={`/project/${projectId}/rfi`}
                  className="text-[11px] text-primary hover:underline font-medium"
                >
                  {t('project360.moreDetails')}
                </Link>
              </div>
              <div className="flex-1 overflow-y-auto px-3 py-2 flex flex-col gap-2">
                {rfisLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                  </div>
                ) : (
                  <>
                    {/* Top stats */}
                    <div className="flex gap-4 pb-2 border-b border-gray-100 shrink-0 flex-wrap">
                      <MiniStat
                        label="Ouverts"
                        value={String(openRfis?.length)}
                        valueColor={openRfis?.length > 0 ? 'text-amber-600' : 'text-gray-700'}
                      />
                      <MiniStat
                        label="Sans réponse"
                        value={String(noResponseRfis?.length)}
                        valueColor={noResponseRfis?.length > 0 ? 'text-red-600' : 'text-gray-700'}
                      />
                      <MiniStat
                        label="Délai moyen"
                        value={avgResponseDays != null ? `${avgResponseDays}j` : '—'}
                        valueColor="text-gray-700"
                      />
                    </div>

                    {/* Open RFIs list */}
                    {openRfis?.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full gap-1">
                        <Icon name="CheckCircle" size={20} className="text-green-500" />
                        <p className="text-[12px] text-green-700 font-medium">{t('rfi.empty.noneOpen')}</p>
                      </div>
                    ) : (
                      <table className="w-full text-[12px] tabular-nums">
                        <thead>
                          <tr className="text-[10px] text-gray-400 uppercase tracking-wide">
                            <th className="text-left pb-1 font-semibold">{t('rfi.pageTitle')}</th>
                            <th className="text-right pb-1 font-semibold">Statut</th>
                            <th className="text-right pb-1 font-semibold">Âge</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {openRfis?.map((r) => (
                            <tr key={r?.id} className="hover:bg-gray-50">
                              <td className="py-1 pr-2 text-[12px] font-medium text-gray-800 truncate max-w-[150px]">
                                <span className="font-medium text-gray-800">#{r?.rfi_number}</span>
                                {r?.subject && <span> · {r?.subject}</span>}
                              </td>
                              <td className="py-1 pr-2 text-right">
                                <RfiStatusPill status={r?.status} />
                              </td>
                              <td className="py-1 text-right">
                                <AgePill days={rfiAgeDays(r)} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </>
                )}
              </div>
              <CardNotesFooter
                cardKey="rfi"
                notes={notesForCard('rfi')}
                userProfile={userProfile}
                projectId={projectId}
                onNotesChange={refreshNotes}
                t={t}
                language={language}
              />
            </div>

          </div>
        </>
      )}
    </div>
  );
};

export default Project360Page;
